import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { CLASS_IMAGES } from "@/content";

function hexToRgba(hex, a) {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}

// Lighten dark accents so text/borders stay readable on the dark card.
// Bright accents (luminance >= 150) are returned unchanged.
function readableAccent(hex) {
  const h = hex.replace("#", "");
  let r = parseInt(h.substring(0, 2), 16);
  let g = parseInt(h.substring(2, 4), 16);
  let b = parseInt(h.substring(4, 6), 16);
  const lum = 0.299 * r + 0.587 * g + 0.114 * b;
  if (lum < 150) {
    const t = Math.min(0.75, (150 - lum) / 180 + 0.28);
    r = Math.round(r + (255 - r) * t);
    g = Math.round(g + (255 - g) * t);
    b = Math.round(b + (255 - b) * t);
  }
  return { css: `rgb(${r},${g},${b})`, border: `rgba(${r},${g},${b},0.5)` };
}

export function ClassCard({ cls, onInterest }) {
  const ref = useRef(null);
  const comingSoon = cls.status === "coming_soon";
  const accent = cls.accent || "#0047FF";
  const readable = readableAccent(accent);
  const image = CLASS_IMAGES[cls.slug];

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - r.left}px`);
    el.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      data-testid={`class-card-${cls.slug}`}
      className={`group relative overflow-hidden rounded-2xl border border-white/8 bg-ink-800 p-7 flex flex-col transition-[transform,border-color] duration-500 hover:-translate-y-1.5 ${
        cls.featured ? "md:col-span-2 md:row-span-1" : ""
      }`}
      style={{ minHeight: cls.featured ? 360 : 300 }}
    >
      {/* faded class still, sitting under the sky blue */}
      {image && (
        <div className="pointer-events-none absolute inset-0">
          <img src={image} alt="" className="faded-media h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${hexToRgba(accent, 0.22)} 0%, rgba(12,44,104,0.42) 45%, rgba(12,44,104,0.82) 100%)` }} />
        </div>
      )}
      {/* cursor glow */}
      <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{ background: `radial-gradient(240px circle at var(--mx,50%) var(--my,50%), ${hexToRgba(accent, 0.22)}, transparent 60%)` }} />
      {/* accent bar */}
      <div className="absolute top-0 left-0 h-1 w-full" style={{ background: `linear-gradient(90deg, ${accent}, transparent)` }} />

      <div className="relative flex items-center justify-between mb-5">
        <span className="label" style={{ color: readable.css }}>
          {comingSoon ? "Coming soon" : (cls.featured ? "Featured strand" : "Available")}
        </span>
        {cls.featured && <Sparkles size={16} style={{ color: readable.css }} />}
      </div>

      <h3 className={`relative font-display font-extrabold tracking-tight text-white ${cls.featured ? "text-4xl sm:text-5xl" : "text-3xl"}`}>
        {cls.name}
      </h3>
      {cls.tagline && <p className="relative text-cyan/80 text-sm mt-2">{cls.tagline}</p>}

      <p className="relative text-ice/70 text-sm leading-relaxed mt-4 flex-1">
        {cls.description}
      </p>

      {cls.leave_feeling?.length > 0 && (
        <div className="relative mt-5 flex flex-wrap gap-2">
          {cls.leave_feeling.map((f) => (
            <span key={f} className="text-[0.7rem] uppercase tracking-wider rounded-full border border-white/15 px-3 py-1 text-ice/80">{f}</span>
          ))}
        </div>
      )}

      <div className="relative mt-7 flex items-center justify-between">
        <Link to={`/classes#${cls.slug}`} data-testid={`class-more-${cls.slug}`}
          className="link-underline text-sm text-ice/80 hover:text-white">Learn more</Link>
        {comingSoon ? (
          <button data-testid={`interest-btn-${cls.slug}`} onClick={() => onInterest(cls)}
            className="inline-flex items-center gap-2 text-sm font-medium rounded-full border px-4 py-2 transition-colors"
            style={{ borderColor: readable.border, color: readable.css }}>
            I'm interested <ArrowRight size={15} />
          </button>
        ) : (
          <Link to="/book" data-testid={`class-book-${cls.slug}`}
            className="inline-flex items-center gap-2 text-sm font-semibold text-white rounded-full px-4 py-2 transition-transform hover:translate-x-0.5"
            style={{ background: hexToRgba(accent, 0.9) }}>
            Book <ArrowRight size={15} />
          </Link>
        )}
      </div>
    </div>
  );
}

export default ClassCard;
